/**
 * The canonical service vocabulary.
 *
 * Authority: NIMER-SCOPE-TAX-001 §1 (codes) and §1.1 (normalisation rules).
 *
 * Service codes are contract keys. They are never surfaced to the customer —
 * the customer-facing labels are owned by TAX-001 (scope-tz6) and by the i18n
 * content, not by this package.
 *
 * PRD §5.4 and §5.7 use `CLOUD_CONFIG_REVIEW` and `EXTERNAL_INFRA_PT`. Those are
 * PRD defects (scope-0bg); TAX-001 wins and neither spelling exists here.
 */
import { z } from 'zod';
import { LongTextSchema, NonEmptyTextSchema } from './primitives.js';
/**
 * Service lines — the portfolio tier above the §1 code taxonomy
 * (IMPL-SVCLINES-001 §2, TAX-001 §0). Every service code below belongs to the
 * `ASSESSMENT` line; the other lines have no discovery branch, no rules and
 * no estimation in v1 — their engagements route to consultant follow-up.
 * Customer-facing labels live in i18n, never here (TAX-001 §1.5's rule for
 * codes holds for lines too).
 */
export const SERVICE_LINES = [
    'ASSESSMENT',
    /*
     * IMPL-ENGAGE-002 §5.4. A creation-time placeholder, not a service: a
     * consultant on a call knows the org and the person but often not yet what
     * is being scoped. It follows every existing non-ASSESSMENT rule with no
     * special-casing — empty `opening_selection`, consultant discovery, no
     * scope package — and resolves to exactly one other line the first time the
     * owner uses the picker. It can never be a terminal value on a submitted
     * engagement (§13 case 22).
     */
    'UNDETERMINED',
    'ARCH_STRATEGY',
    'GRC',
    'OT_ICS',
    'INFRA_SEC',
    'SEC_OPS',
    'AI_SEC',
    'ADVISORY',
];
export const ServiceLineSchema = z.enum(SERVICE_LINES);
/**
 * Concrete service codes — everything that may appear in a final scope package.
 */
export const CONCRETE_SERVICE_CODES = [
    'WEB_APP_PT',
    'API_PT',
    'MOB_PT',
    'THICK_PT',
    'EXT_INFRA_PT',
    'INT_INFRA_PT',
    'AD_SEC_ASSESS',
    'ENTRA_SEC_ASSESS',
    'IAM_REVIEW',
    'CLOUD_SEC_AZURE',
    'CLOUD_SEC_AWS',
    'CLOUD_SEC_GCP',
    'EPT_SEC_REVIEW',
    'RED_TEAM_SIM',
    'WLS_SEC_ASSESS',
    'CFG_REV_FW',
    'CFG_REV_OS',
    'CFG_REV_DB',
    'CFG_REV_NETDEV',
    'VA_EXT',
    'VA_INT',
    'VA_AUTH',
    'OT_VA',
    'OT_PT',
    'SOC_ENG_SIM',
    'CICD_SEC_REVIEW',
];
/**
 * `COMBINED` is a **meta-code** (TAX-001 §1.1.1). The classifier may hold it
 * during discovery; it must never reach a scope package's DERIVED ASSESSMENT.
 * That is why it is not in {@link CONCRETE_SERVICE_CODES} — the scope-package
 * schema accepts only concrete codes, so the rule is enforced by the type
 * rather than by a reviewer noticing.
 */
export const META_SERVICE_CODES = ['COMBINED'];
export const SERVICE_CODES = [...CONCRETE_SERVICE_CODES, ...META_SERVICE_CODES];
export const ConcreteServiceCodeSchema = z.enum(CONCRETE_SERVICE_CODES);
export const ServiceCodeSchema = z.enum(SERVICE_CODES);
/**
 * A templated service code, as authored in a `for_each`-bound rule or signal
 * before instantiation (DM-001 §2.2.4) — `CLOUD_SEC_<provider>`,
 * `CFG_REV_<class>`. The bound identifier is substituted and upper-cased at
 * instantiation time; whether the *result* is a real TAX-001 §1 code is a
 * pack-validator concern, not a shape concern, so this schema only recognises
 * the textual form: an upper-case, underscore-separated prefix immediately
 * followed by one `<lowercase_binding>` placeholder.
 */
export const ServiceCodeTemplateSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*_<[a-z][a-z0-9_]*>$/);
/**
 * The shape of a `service` field as it appears in pack content (a rule's
 * `then` action, or a question's `signals` entry): a concrete code, or —
 * when the containing rule/signal declares a `for_each` — a template that
 * has not yet been substituted (DM-001 §2.2.4). Un-instantiated pack content
 * is exactly where the template form appears; a resolved firing or candidate
 * row always carries a concrete {@link ServiceCodeSchema} instead.
 */
export const ServiceCodeOrTemplateSchema = z.union([ServiceCodeSchema, ServiceCodeTemplateSchema]);
/**
 * Per-instance discriminator for services that instantiate more than once
 * (TAX-001 §1.1.3–.5): `MOB_PT[android]`, `WLS_SEC_ASSESS[site:2]`,
 * `CFG_REV_FW[device:edge-01]`. `null` means the single, unqualified instance.
 *
 * This is the rule-authoring / binding spelling (`rules.ts`'s `instance`), not
 * the stored-row spelling — see {@link StoredInstanceKeySchema}.
 */
export const InstanceKeySchema = z.string().regex(/^[a-z][a-z0-9_]*(?::[a-z0-9_-]+)?$/);
/**
 * A templated `instance` value, as authored in a `for_each`-bound rule's
 * `then` action before instantiation (DM-001 §2.2.4). Substitution into an
 * instance key is verbatim, so the template is the binding placeholder
 * itself — either bare (`<provider>`, the collection form) or prefixed
 * (`app:<i>`, `site:<n>`, the integer-range form). The prefix, when present,
 * matches an ordinary instance-key segment.
 */
export const InstanceKeyTemplateSchema = z
    .string()
    .regex(/^(?:[a-z][a-z0-9_-]*:)?<[a-z][a-z0-9_]*>$/);
/**
 * The shape of an `instance` field as it appears in pack content (a rule's
 * `then` action): a concrete instance key, or — when the containing rule
 * declares a `for_each` — a template that has not yet been substituted
 * (DM-001 §2.2.4). A resolved firing or candidate row always carries a
 * concrete {@link InstanceKeySchema} instead.
 */
export const InstanceKeyOrTemplateSchema = z.union([InstanceKeySchema, InstanceKeyTemplateSchema]);
/**
 * A stored candidate/derived-assessment row's `instance_key`, exactly as
 * DM-001 §4's DDL spells it: `NOT NULL DEFAULT ''`, never NULL, because a
 * nullable `instance_key` defeats the natural-key unique constraint (Postgres
 * treats NULLs as distinct, so the upsert never conflicts and duplicates
 * accrue). `''` is the engagement-wide, unqualified instance.
 */
export const StoredInstanceKeySchema = z.union([z.literal(''), InstanceKeySchema]);
/** Branch codes — the domain → schema-file map, TAX-001 §2. */
export const PACK_KEYS = [
    'GEN',
    'APP',
    'INF',
    'IDN',
    'CLD',
    'EPT',
    'WLS',
    'CFG',
    'VUL',
    'OTX',
    'CMB',
];
export const PackKeySchema = z.enum(PACK_KEYS);
/**
 * Opening-screen options (TAX-001 §3). Multi-select; options 8–10 route rather
 * than open a branch directly.
 */
export const OPENING_OPTIONS = [
    'website_portal_application',
    'mobile_application',
    'apis_or_integration_services',
    'servers_or_network_infrastructure',
    'cloud_environment',
    'identity_environment',
    'wireless_network',
    'multiple_systems_or_entire_environment',
    'audit_or_compliance_requirement',
    'not_sure',
];
export const OpeningOptionSchema = z.enum(OPENING_OPTIONS);
/**
 * `GET /api/v1/catalog` (IMPL-SVCLINES-001 §6.2) — the active services pack
 * projected for the pre-session picker: label and description only. No
 * service codes (never surfaced to customers, TAX-001 §1.5), no prices, no
 * engagement pinning — this is the public shop window, not the staff read.
 */
export const PublicCatalogEntrySchema = z.object({
    label: NonEmptyTextSchema,
    description: LongTextSchema,
});
export const PublicCatalogSchema = z.array(PublicCatalogEntrySchema);
//# sourceMappingURL=service.js.map